import { useEffect, useMemo, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { haptic } from '../engine/haptics.ts';
import { CREAM, INK, LINE, MUTED, ORANGE, PAPER, SHADOW_CARD, MOTION, CORAL_DEEP } from '../ui/theme.ts';

/**
 * search-select: a search field over `options`; with no query the `popular`
 * subset shows, and `pinned` (e.g. "Mixed / not sure") always sits at the end.
 * Tap = select + advance. Answer: { value, label, index }.
 */
export function SearchSelect({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  const options = content.options ?? [];
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ctx.setCta({ label: null });
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [step.id]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) return options.filter((o) => o.label.toLowerCase().includes(q));
    const popular = content.popular ?? [];
    if (!popular.length) return options;
    return popular.map((v) => options.find((o) => o.value === v)).filter((o): o is NonNullable<typeof o> => !!o);
  }, [query, options, content.popular]);

  const pick = (value: string, label: string, index: number) => {
    haptic();
    setSelected(value);
    ctx.setAnswer(key, { value, label, index });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ctx.next(value), step.autoAdvanceMs ?? MOTION.autoAdvance);
  };

  const q = query.trim();
  const pinned = content.pinned;

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ position: 'relative' }}>
        <Icon name="magnifying-glass" size={20} color={MUTED} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          className="fk-input"
          type="search"
          autoComplete="off"
          value={query}
          aria-label={content.searchPlaceholder ?? 'Search'}
          placeholder={content.searchPlaceholder ?? 'Search'}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '100%', minHeight: 54, padding: '10px 16px 10px 46px', borderRadius: 16, font: 'inherit', fontWeight: 700, fontSize: 16, background: PAPER, color: INK, border: `2px solid ${LINE}`, boxShadow: SHADOW_CARD }}
        />
      </div>

      {!q && content.popularLabel ? (
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: CORAL_DEEP }}>{content.popularLabel}</div>
      ) : null}

      <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map((o) => (
          <Row key={o.value} label={o.label} emoji={o.emoji} selected={selected === o.value} onSelect={() => pick(o.value, o.label, options.indexOf(o))} />
        ))}
        {q && !shown.length ? <div style={{ fontSize: 14, fontWeight: 600, color: MUTED, textAlign: 'center', padding: 8 }}>—</div> : null}
        {pinned ? <Row label={pinned.label} emoji={pinned.emoji ?? '🐾'} selected={selected === pinned.value} onSelect={() => pick(pinned.value, pinned.label, -1)} pinned /> : null}
      </div>
    </>
  );
}

function Row({ label, emoji, selected, onSelect, pinned = false }: { label: string; emoji?: string; selected: boolean; onSelect: () => void; pinned?: boolean }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 52,
        padding: '10px 14px',
        borderRadius: 16,
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 700,
        fontSize: 15,
        color: INK,
        background: selected ? CREAM : pinned ? CREAM : PAPER,
        border: `${pinned ? 1.5 : 1.5}px ${pinned ? 'dashed' : 'solid'} ${selected ? ORANGE : LINE}`,
        transition: 'all .15s',
      }}
    >
      {emoji ? (
        <span aria-hidden="true" style={{ fontSize: 18 }}>
          {emoji}
        </span>
      ) : null}
      <span style={{ flex: 1 }}>{label}</span>
      {selected ? <Icon name="check-circle" size={20} color={ORANGE} /> : null}
    </button>
  );
}
